/**
 * Step 4 do wizard ZTNA — Review + Provisionar.
 *
 * Mostra:
 *   - Resumo read-only de Step1+2+3.
 *   - PreflightChecklist (carregado on-mount).
 *   - PlanExecutionList (estatico).
 *   - Checkbox de confirmacao + botao "Provisionar agora".
 *
 * O caller (WizardContainer) recebe o handler de submit e o status do
 * preflight via props.
 */
import { useEffect } from 'react'
import { Badge, Card, Checkbox, Code, Group, Stack, Text, Title } from '@mantine/core'
import { PlanExecutionList } from './PlanExecutionList'
import { PreflightChecklist } from './PreflightChecklist'
import { PreflightPanel } from './PreflightPanel'
import { DnsConflictBanner } from './DnsConflictBanner'
import type { PreflightResponse } from '../types'
import type { DnsMigrationState, WizardState } from './wizardState'

interface Props {
  state: WizardState
  preflight: PreflightResponse | null | undefined
  preflightLoading?: boolean
  preflightError?: Error | null
  onChangeStep4: (next: WizardState['step4']) => void
  onChangeDnsMigration?: (next: DnsMigrationState) => void
  onRunPreflight: () => void
}

export function Step4Review({
  state,
  preflight,
  preflightLoading,
  preflightError,
  onChangeStep4,
  onChangeDnsMigration,
  onRunPreflight,
}: Props) {
  // Roda preflight automaticamente ao chegar no Step 4.
  useEffect(() => {
    onRunPreflight()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { step1, step2, step3, step4 } = state

  // Sub-projeto 4 Lote 2 — DNS conflict banner. Render apenas se preflight
  // check #10 detectou colisao (record_type !== 'NONE'). Com o check reprovado
  // (registro com politica de roteamento, consoleai#24704) nao ha migracao a
  // oferecer: o motivo aparece no painel de preflight.
  const dnsConflictCheck = preflight?.checks.find(
    (c) => c.name === 'dns_conflict',
  )
  const dnsConflict = dnsConflictCheck?.conflict
  const showDnsBanner =
    dnsConflict != null &&
    dnsConflict.record_type !== 'NONE' &&
    dnsConflictCheck?.status !== 'fail' &&
    onChangeDnsMigration != null

  return (
    <Stack gap="md">
      <Card withBorder padding="lg" radius="md">
        <Title order={4} mb="sm">Resumo do cadastro</Title>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '160px 1fr',
            gap: '8px 16px',
            fontSize: '0.86rem',
          }}
        >
          <Text c="dimmed">Tenant</Text>
          <Text>
            <strong>{step1.tenant_name ?? '—'}</strong>
            {step1.tenant_id != null ? (
              <Text component="span" size="xs" c="dimmed" ff="JetBrains Mono, monospace" ml={6}>
                id {step1.tenant_id}
              </Text>
            ) : null}
          </Text>

          <Text c="dimmed">Zona Route53</Text>
          <Text ff="JetBrains Mono, monospace">{step1.zone_name ?? '—'}</Text>

          <Text c="dimmed">Hostname público</Text>
          <Text
            ff="JetBrains Mono, monospace"
            fw={600}
            c="var(--ai-brand-azul, #025468)"
          >
            {step1.public_hostname || '—'}
          </Text>

          <Text c="dimmed">Hostname canonical</Text>
          <Text ff="JetBrains Mono, monospace" size="xs" c="dimmed">
            {step2.canonical_hostname || '—'}
          </Text>

          <Text c="dimmed">Slug</Text>
          <Text ff="JetBrains Mono, monospace">{step2.slug || '—'}</Text>

          <Text c="dimmed">Display name</Text>
          <Text>{step2.display_name || '—'}</Text>

          <Text c="dimmed">Ambiente</Text>
          <div>
            {step1.environment ? (
              <Badge color="blue" variant="light">{step1.environment}</Badge>
            ) : '—'}
          </div>

          <Text c="dimmed">Protocolo / Routing</Text>
          <Group gap={4}>
            <Badge color="blue" variant="light">{step3.protocol.toUpperCase()}</Badge>
            <Badge color="blue" variant="light">{step3.routing_mode.toUpperCase()}</Badge>
            <Badge color="blue" variant="light">{step3.identity_mode.toUpperCase()}</Badge>
          </Group>

          <Text c="dimmed">Upstream URL</Text>
          <Text ff="JetBrains Mono, monospace" size="xs">{step3.upstream_url || '—'}</Text>

          <Text c="dimmed">Visível a todos da org</Text>
          <Text size="xs">{step3.org_default_visible ? 'Sim (default)' : 'Não — requer permissão explícita'}</Text>

          <Text c="dimmed">Privado</Text>
          <Text size="xs">{step3.is_private ? 'Sim — restrito à organização' : 'Não — público'}</Text>

          <Text c="dimmed">Permissions necessárias</Text>
          <Group gap={4}>
            {step3.required_permissions.length === 0 ? (
              <Text size="xs" c="dimmed">nenhuma exigida</Text>
            ) : (
              step3.required_permissions.map((p) => <Code key={p}>{p}</Code>)
            )}
          </Group>
        </div>
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Title order={4} mb="sm">Pre-flight checks</Title>
        <PreflightChecklist
          result={preflight}
          isLoading={preflightLoading}
          error={preflightError ?? null}
        />
        <PreflightPanel
          result={preflight}
          loading={preflightLoading}
        />
      </Card>

      {showDnsBanner && dnsConflict ? (
        <DnsConflictBanner
          conflict={dnsConflict}
          state={state.dnsMigration}
          onChange={onChangeDnsMigration!}
        />
      ) : null}

      <Card withBorder padding="lg" radius="md">
        <Title order={4} mb="xs">Plano de execução</Title>
        <Text size="sm" c="dimmed" mb="sm">
          Worker assíncrono executara as 7 etapas em sequência. Estimativa total: <strong>3-6 minutos</strong>.
          Em qualquer falha, rollback automático em ordem reversa.
        </Text>
        <PlanExecutionList />
      </Card>

      <Card withBorder padding="md" radius="md">
        <Checkbox
          checked={step4.confirmed_upstream_headers}
          onChange={(e) =>
            onChangeStep4({ confirmed_upstream_headers: e.currentTarget.checked })
          }
          label={
            <span>
              <strong>Confirmo que o upstream aceita headers ZTNA injetados</strong>
              <Text size="xs" c="dimmed" mt={2}>
                O app reconhece <Code>X-Forwarded-User</Code>/<Code>X-Forwarded-Email</Code> e
                bloqueia acesso direto fora do broker.
              </Text>
            </span>
          }
        />
      </Card>
    </Stack>
  )
}
